import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Building2, FileText, MapPin, Phone, User } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { useToast } from "@/shared/providers/ToastProvider";
import { ApiError } from "@/shared/lib/apiClient";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { FormField, inputClass, selectClass, textareaClass } from "../components/FormField";
import {
  createAdminProvider,
  fetchAdminProvider,
  updateAdminProvider,
  type AdminProviderPayload,
} from "../api/adminApi";
import { getAuthToken } from "../auth";

type ProviderFormPageProps = {
  mode: "create" | "edit";
};

const emptyForm = {
  commercial_name: "",
  type: "",
  description: "",
  address: "",
  city: "",
  province: "",
  postal_code: "",
  phone: "",
  email: "",
  website: "",
  contact_name: "",
  contact_phone: "",
  contact_email: "",
  agreement_number: "",
  rccm: "",
  id_nat: "",
  specialties: "",
  status: "active",
};

export function ProviderFormPage({ mode }: ProviderFormPageProps) {
  const { t } = useTranslation();
  const labels = t.safecheck.providers.create;
  const { showToast } = useToast();
  const navigate = useNavigate();
  const params = useParams();
  const providerId = mode === "edit" ? Number(params.id) : null;

  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(mode === "edit");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setField = (key: keyof typeof emptyForm, value: string) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  useEffect(() => {
    if (mode !== "edit" || !providerId || Number.isNaN(providerId)) return;
    if (!getAuthToken()) {
      setError("Connexion admin requise.");
      setLoading(false);
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const provider = await fetchAdminProvider(providerId);
        if (cancelled) return;
        setForm({
          commercial_name: provider.commercial_name ?? "",
          type: provider.type_raw || "",
          description: provider.description ?? "",
          address: provider.address ?? "",
          city: provider.city ?? "",
          province: provider.province ?? "",
          postal_code: provider.postal_code ?? "",
          phone: provider.phone ?? "",
          email: provider.email ?? "",
          website: provider.website ?? "",
          contact_name: provider.contact_name ?? "",
          contact_phone: provider.contact_phone ?? "",
          contact_email: provider.contact_email ?? "",
          agreement_number: provider.agreement_number ?? "",
          rccm: provider.rccm ?? "",
          id_nat: provider.id_nat ?? "",
          specialties: provider.specialties ?? "",
          status: provider.status === "inactive" ? "inactive" : "active",
        });
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Prestataire introuvable.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [mode, providerId]);

  const handleSubmit = async () => {
    setError(null);
    if (!form.commercial_name.trim() || !form.type) {
      setError("Nom commercial et type sont obligatoires.");
      return;
    }
    if (!getAuthToken()) {
      setError("Connexion admin requise.");
      return;
    }

    const payload: AdminProviderPayload = {
      commercial_name: form.commercial_name.trim(),
      type: form.type,
      status: form.status,
      description: form.description.trim(),
      address: form.address.trim(),
      city: form.city.trim(),
      province: form.province.trim(),
      postal_code: form.postal_code.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      website: form.website.trim(),
      contact_name: form.contact_name.trim(),
      contact_phone: form.contact_phone.trim(),
      contact_email: form.contact_email.trim(),
      agreement_number: form.agreement_number.trim(),
      rccm: form.rccm.trim(),
      id_nat: form.id_nat.trim(),
      specialties: form.specialties.trim(),
    };

    setSaving(true);
    try {
      if (mode === "create") {
        await createAdminProvider(payload);
      } else if (providerId) {
        await updateAdminProvider(providerId, payload);
      }
      showToast(t.actions.saved);
      navigate("/dashboard/providers");
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Enregistrement impossible.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Breadcrumbs
        items={[
          { label: t.safecheck.nav.providers, href: "/dashboard/providers" },
          { label: mode === "create" ? labels.title : "Modifier" },
        ]}
      />

      <div className="mb-4 flex justify-end">
        <Link
          to="/dashboard/providers"
          className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-card-muted"
        >
          <ArrowLeft size={14} />
          {labels.backToList}
        </Link>
      </div>

      <div className="theme-card p-6">
        <h1 className="mb-5 text-xl font-semibold text-foreground">
          {mode === "create" ? labels.cardTitle : "Modifier le prestataire"}
        </h1>

        {loading ? <p className="mb-4 text-sm text-muted">Chargement…</p> : null}
        {error ? <p className="mb-4 text-sm text-red-600">{error}</p> : null}

        <div className="mb-4 flex items-center gap-2 text-sm font-medium text-primary">
          <Building2 size={14} />
          <span>{labels.baseInfo}</span>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <FormField label={labels.fields.commercialName} required className="md:col-span-2">
            <input
              value={form.commercial_name}
              onChange={(e) => setField("commercial_name", e.target.value)}
              placeholder={labels.placeholders.commercialName}
              className={inputClass}
              disabled={loading || saving}
            />
          </FormField>
          <FormField label={labels.fields.providerType} required>
            <select
              className={selectClass}
              value={form.type}
              onChange={(e) => setField("type", e.target.value)}
              disabled={loading || saving}
            >
              <option value="">{labels.placeholders.providerType}</option>
              <option value="hopital">Hôpital</option>
              <option value="clinique">Clinique</option>
              <option value="pharmacie">Pharmacie</option>
              <option value="cabinet_medical">Cabinet médical</option>
              <option value="laboratoire">Laboratoire</option>
            </select>
          </FormField>
          <FormField label={labels.fields.description} className="md:col-span-3">
            <textarea
              value={form.description}
              onChange={(e) => setField("description", e.target.value)}
              placeholder={labels.placeholders.description}
              className={textareaClass}
              disabled={loading || saving}
            />
          </FormField>
        </div>

        <div className="my-5 border-t border-border" />

        <div className="mb-4 flex items-center gap-2 text-sm font-medium text-primary">
          <MapPin size={14} />
          <span>{labels.locationInfo}</span>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <FormField label={labels.fields.address} className="md:col-span-3">
            <textarea
              value={form.address}
              onChange={(e) => setField("address", e.target.value)}
              placeholder={labels.placeholders.address}
              className="min-h-[84px] w-full rounded-lg border border-border bg-card px-3 py-2.5 text-sm text-foreground outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20"
              disabled={loading || saving}
            />
          </FormField>
          <FormField label={labels.fields.city}>
            <input
              value={form.city}
              onChange={(e) => setField("city", e.target.value)}
              placeholder={labels.placeholders.city}
              className={inputClass}
              disabled={loading || saving}
            />
          </FormField>
          <FormField label={labels.fields.province}>
            <input
              value={form.province}
              onChange={(e) => setField("province", e.target.value)}
              placeholder={labels.placeholders.province}
              className={inputClass}
              disabled={loading || saving}
            />
          </FormField>
          <FormField label={labels.fields.postalCode}>
            <input
              value={form.postal_code}
              onChange={(e) => setField("postal_code", e.target.value)}
              placeholder={labels.placeholders.postalCode}
              className={inputClass}
              disabled={loading || saving}
            />
          </FormField>
        </div>

        <div className="my-5 border-t border-border" />

        <div className="mb-4 flex items-center gap-2 text-sm font-medium text-primary">
          <Phone size={14} />
          <span>{labels.contactInfo}</span>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <FormField label={labels.fields.phone}>
            <input
              value={form.phone}
              onChange={(e) => setField("phone", e.target.value)}
              placeholder={labels.placeholders.phone}
              className={inputClass}
              disabled={loading || saving}
            />
          </FormField>
          <FormField label={labels.fields.email}>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setField("email", e.target.value)}
              placeholder={labels.placeholders.email}
              className={inputClass}
              disabled={loading || saving}
            />
          </FormField>
          <FormField label={labels.fields.website}>
            <input
              value={form.website}
              onChange={(e) => setField("website", e.target.value)}
              placeholder={labels.placeholders.website}
              className={inputClass}
              disabled={loading || saving}
            />
          </FormField>
        </div>

        <div className="my-5 border-t border-border" />

        <div className="mb-4 flex items-center gap-2 text-sm font-medium text-primary">
          <User size={14} />
          <span>{labels.contactPersonInfo}</span>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <FormField label={labels.fields.contactName}>
            <input
              value={form.contact_name}
              onChange={(e) => setField("contact_name", e.target.value)}
              placeholder={labels.placeholders.contactName}
              className={inputClass}
              disabled={loading || saving}
            />
          </FormField>
          <FormField label={labels.fields.contactPhone}>
            <input
              value={form.contact_phone}
              onChange={(e) => setField("contact_phone", e.target.value)}
              placeholder={labels.placeholders.contactPhone}
              className={inputClass}
              disabled={loading || saving}
            />
          </FormField>
          <FormField label={labels.fields.contactEmail}>
            <input
              type="email"
              value={form.contact_email}
              onChange={(e) => setField("contact_email", e.target.value)}
              placeholder={labels.placeholders.contactEmail}
              className={inputClass}
              disabled={loading || saving}
            />
          </FormField>
        </div>

        <div className="my-5 border-t border-border" />

        <div className="mb-4 flex items-center gap-2 text-sm font-medium text-primary">
          <FileText size={14} />
          <span>{labels.administrativeInfo}</span>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <FormField label={labels.fields.agreementNumber}>
            <input
              value={form.agreement_number}
              onChange={(e) => setField("agreement_number", e.target.value)}
              placeholder={labels.placeholders.agreementNumber}
              className={inputClass}
              disabled={loading || saving}
            />
          </FormField>
          <FormField label={labels.fields.rccm}>
            <input
              value={form.rccm}
              onChange={(e) => setField("rccm", e.target.value)}
              placeholder={labels.placeholders.rccm}
              className={inputClass}
              disabled={loading || saving}
            />
          </FormField>
          <FormField label={labels.fields.idNat}>
            <input
              value={form.id_nat}
              onChange={(e) => setField("id_nat", e.target.value)}
              placeholder={labels.placeholders.idNat}
              className={inputClass}
              disabled={loading || saving}
            />
          </FormField>
          <FormField label={labels.fields.specialties} className="md:col-span-3" hint={labels.specialtiesHint}>
            <input
              value={form.specialties}
              onChange={(e) => setField("specialties", e.target.value)}
              placeholder={labels.placeholders.specialties}
              className={inputClass}
              disabled={loading || saving}
            />
          </FormField>
          <FormField label={labels.fields.status} required className="md:max-w-xs">
            <select
              className={selectClass}
              value={form.status}
              onChange={(e) => setField("status", e.target.value)}
              disabled={loading || saving}
            >
              <option value="active">Actif</option>
              <option value="inactive">Inactif</option>
            </select>
          </FormField>
        </div>

        <div className="mt-8 flex items-center justify-between">
          <Link
            to="/dashboard/providers"
            className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm text-muted-foreground transition-colors hover:bg-card-muted"
          >
            {t.safecheck.cancel}
          </Link>
          <button
            type="button"
            disabled={loading || saving}
            onClick={() => void handleSubmit()}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:opacity-60"
          >
            {saving ? "Enregistrement…" : labels.submit}
          </button>
        </div>
      </div>
    </>
  );
}

export function AddProviderPage() {
  return <ProviderFormPage mode="create" />;
}

export function EditProviderPage() {
  return <ProviderFormPage mode="edit" />;
}
